import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { CONSENT_CHANGED_EVENT, hasConsent } from "@/lib/consent";

const GTM_ID = "GTM-WQWKCG85";
const SCRIPT_ID = "gtm-script";

type W = Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };

function gtag(...args: unknown[]) {
  const w = window as W;
  w.dataLayer = w.dataLayer || [];
  // gtag erwartet das arguments-Objekt
  // eslint-disable-next-line prefer-rest-params
  w.dataLayer.push(arguments);
}

function consentState() {
  const marketing = hasConsent("googleTagManager") || hasConsent("googleAds") || hasConsent("googleAnalytics");
  const analytics = hasConsent("googleAnalytics") || hasConsent("googleTagManager");
  const v = (b: boolean) => (b ? "granted" : "denied");
  return {
    ad_storage: v(marketing),
    ad_user_data: v(marketing),
    ad_personalization: v(marketing),
    analytics_storage: v(analytics),
  };
}

/** Google Consent Mode v2: GTM lädt immer, Cookies nur nach Zustimmung. */
function initGTM() {
  const w = window as W;
  if (document.getElementById(SCRIPT_ID)) return;
  w.gtag = gtag;
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });
  gtag("set", "ads_data_redaction", true);
  gtag("consent", "update", consentState());

  w.dataLayer!.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);
}

export function GoogleTagManager() {
  const location = useLocation();
  const first = useRef(true);

  useEffect(() => {
    initGTM();
    const apply = () => {
      gtag("consent", "update", consentState());
      (window as W).dataLayer!.push({ event: "consent_update" });
    };
    window.addEventListener(CONSENT_CHANGED_EVENT, apply);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, apply);
  }, []);

  // Virtuelle Seitenaufrufe für React-Routen (erster Aufruf zählt GTM selbst)
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    (window as W).dataLayer?.push({
      event: "page_view",
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);

  return null;
}
